import { useQuery } from 'react-query';
import { Filters } from '../../models';
import { ArticleTableRow } from '../../models/ArticleTableRow';
import { fetchCovenantsSearch, fetchNewsSearch, fetchResearchSearch } from '../../services/articles/articles.service';
import { ArticleListDto } from '../../services/dtos/article/articleList.dto';
import { SearchFeedRequestBodyDto } from '../../services/dtos/article/searchFeedRequestBody.dto';
import { IssuerSearchResultDto, IssuerSearchResultItemDto } from '../../services/dtos/issuer/issuerSearchResult.dto';
import { SearchRequestBodyDto } from '../../services/dtos/search/searchRequestBody.dto';
import { fetchIssuerSearch } from '../../services/issuers/issuers.service';
import { SortOption } from '../../services/services.constants';
import { getGroupedFilterTags } from '../../utils/filterUtils';
import { sanitizeElasticSearchStringValue } from '../../utils/sanitizeElasticSearchStringValue';
import { mapArticleListItemDtoToArticleTableRow } from '../articles/utils/article.mappers';
import { queryKeys } from '../queryKeyFactory';

export enum SearchContentType {
  research = 'research',
  news = 'news',
  covenants = 'covenants',
  companies = 'companies',
}

export interface UseFetchSearchQuery {
  isLoading: boolean;
  data?: (ArticleTableRow | IssuerSearchResultItemDto)[];
  isError: boolean;
  refetch: () => void;
  totalResults?: number;
  totalPages: number;
}

export default (
  contentType: SearchContentType,
  filters: Filters | null,
  take: number,
  page: number,
  searchTerm: string,
  sort: SortOption | undefined,
  enabled: boolean
): UseFetchSearchQuery => {
  const queryFn = getQueryFnByContentType(contentType);
  const appliedFilters: SearchFeedRequestBodyDto = {
    searchTerm: sanitizeElasticSearchStringValue(searchTerm.trim()),
    startDate: filters?.startDate,
    endDate: filters?.endDate,
    tags: getGroupedFilterTags(filters),
    sort,
    take,
  };
  let totalResults = 0;

  const query = useQuery(queryKeys.fetchSearches.allByContentType(contentType, appliedFilters, page), queryFn, {
    enabled,
    meta: { filters: appliedFilters, page: page - 1 },
    select: (searchResult) => {
      if (!searchResult?.results) {
        return undefined;
      }
      totalResults = searchResult.totalResults;
      let results;
      if (isFeedSearch(contentType)) {
        const articlesList = searchResult as ArticleListDto;
        results = mapArticleListItemDtoToArticleTableRow(articlesList.results);
      } else {
        const companiesList = searchResult as IssuerSearchResultDto;
        results = companiesList.results;
      }

      return {
        results,
        totalPages: Math.ceil(totalResults / take),
      };
    },
  });

  return {
    isError: query.isError,
    data: query.data?.results,
    isLoading: !query.isFetched || query.isLoading,
    refetch: query.refetch,
    totalPages: query.data?.totalPages || 0,
    totalResults,
  };
};

const getQueryFnByContentType = (
  contentType: SearchContentType
): (({ meta }: { meta: any }) => Promise<ArticleListDto | IssuerSearchResultDto>) => {
  let queryFn: (requestBody: SearchRequestBodyDto, page: number) => Promise<ArticleListDto | IssuerSearchResultDto>;
  switch (contentType) {
    case SearchContentType.companies:
      queryFn = fetchIssuerSearch;
      break;
    case SearchContentType.covenants:
      queryFn = fetchCovenantsSearch;
      break;
    case SearchContentType.news:
      queryFn = fetchNewsSearch;
      break;
    case SearchContentType.research:
      queryFn = fetchResearchSearch;
      break;
    default:
      throw new Error(`${contentType} is not supported`);
  }

  return ({ meta }) => queryFn(meta.filters, meta.page);
};

const isFeedSearch = (contentType: SearchContentType): boolean =>
  [SearchContentType.news, SearchContentType.covenants, SearchContentType.research].includes(contentType);
